import { FieldProse, obj } from './DossierPrimitives';

/**
 * USA 510(k)-Exempt QMS & Technical Documentation package renderer.
 *
 * The device makes NO submission to the FDA — no predicate, no 510(k), no
 * substantial-equivalence comparison. The dossier is the INTERNAL Medical
 * Device File (ISO 13485 Cl. 4.2.3) a certification body / FDA investigator
 * would read, preceded by the 6 applicability determinations that establish
 * the exemption (REG-QMS-ARC-001 §4).
 *
 * Schema detection (in the parent renderer): submission_type === "510k-exempt".
 *
 * Every field renders regardless of whether the agent filled it in — a
 * missing/placeholder value falls back to the shared "Submitter must
 * complete" chip (via FieldProse) so the reviewer sees the full roster of
 * expected sections, not just the ones the agent happened to populate.
 *
 * `ivd` is accepted in the prop type (not destructured — unused in v1) for
 * call-signature parity with the other device-pathway renderers (Pma /
 * DeNovo / Estar), which the parent dispatches with `ivd={isIvd}`. The IVD
 * variant schema (`Estar510kExemptIvdDossier` in backend/schemas_ivd.py) is
 * an empty subclass — the IVD framing is prompt-driven, not schema-driven —
 * so there is no rendering branch on it here.
 *
 * LOCKSTEP with backend/services/dossier/estar_renderer/_510k_exempt.py —
 * the two title maps below (key → title, in the same order as the schema's
 * `GATE_KEYS` / `MDF_KEYS` in backend/schemas_510k_exempt.py) must mirror
 * that renderer's `_GATE_TITLES` / `_MDF_TITLES` exactly, or the downloaded
 * PDF and the in-app result view will show different section names/order
 * for the same run.
 *
 * The Design & Development File (`design_development_file` / `DDF_KEYS` in
 * the schema) is declared for phase-2 shape stability; v1 leaves it null —
 * intentionally omitted here.
 *
 * Outcome banner: the top-level `exemption_outcome` field ("exempt" |
 * "not_exempt" | "not_grounded" | null) drives a prominent banner above the
 * two groups. Fail-safe: anything other than an exact "exempt" — including
 * a missing/null value — renders the NOT-exempt warning, never a false
 * assertion of exemption. LOCKSTEP with the `is_exempt` fail-safe in
 * backend/services/dossier/estar_renderer/_510k_exempt.py.
 */
export function The510kExemptRenderer({
  dossier,
}: {
  dossier: Record<string, unknown>;
  ivd?: boolean;
}) {
  const determinations = obj(dossier, 'determinations');
  const mdf = obj(dossier, 'medical_device_file');
  const isExempt =
    String((dossier as Record<string, unknown>)['exemption_outcome'] ?? '').toLowerCase() ===
    'exempt';
  return (
    <div className="space-y-4">
      {isExempt ? (
        <div className="rounded-xl border border-state-success-border bg-state-success-bg text-state-success-text py-3 px-4 text-[14px] font-semibold">
          510(k)-Exempt — Medical Device File (no FDA premarket submission required)
        </div>
      ) : (
        <div className="rounded-xl border border-state-warning-border bg-state-warning-bg text-state-warning-text py-3 px-4 text-[14px] font-semibold">
          NOT 510(k)-exempt — US premarket authorization (510(k)/De Novo) required before
          commercial distribution. This is a Medical Device File / QMS record, not a submission.
        </div>
      )}
      <Group title="Determinations" keys={GATE_KEYS} titles={GATE_TITLES} data={determinations} />
      <Group title="Medical Device File" keys={MDF_KEYS} titles={MDF_TITLES} data={mdf} />
    </div>
  );
}

// LOCKSTEP with _510k_exempt.py
const GATE_KEYS = [
  'exemption_and_limitations',
  'cgmp_applicability',
  'design_control_applicability',
  'other_exclusions',
  'udi_applicability',
  'software_applicability_and_class',
] as const;

// LOCKSTEP with _510k_exempt.py
const GATE_TITLES: Record<string, string> = {
  exemption_and_limitations: 'Exemption & Limitations',
  cgmp_applicability: 'cGMP / QMSR Applicability',
  design_control_applicability: 'Design Control Applicability',
  other_exclusions: 'Other Exclusions',
  udi_applicability: 'UDI Applicability',
  software_applicability_and_class: 'Software Applicability & Class',
};

// LOCKSTEP with _510k_exempt.py
const MDF_KEYS = [
  'mdf1_device_identification',
  'mdf2_regulatory_status',
  'mdf3_description_and_intended_use',
  'mdf4_labelling',
  'mdf5_product_specifications',
  'mdf6_manufacturing',
  'mdf7_measurement_and_monitoring',
  'mdf8_installation',
  'mdf9_servicing',
  'mdf10_risk_management_summary',
  'mdf11_standards_applied',
  'mdf12_index_and_revision_control',
] as const;

// LOCKSTEP with _510k_exempt.py
const MDF_TITLES: Record<string, string> = {
  mdf1_device_identification: '1. Device Identification',
  mdf2_regulatory_status: '2. Regulatory Status',
  mdf3_description_and_intended_use: '3. Description & Intended Use',
  mdf4_labelling: '4. Labelling',
  mdf5_product_specifications: '5. Product Specifications',
  mdf6_manufacturing: '6. Manufacturing',
  mdf7_measurement_and_monitoring: '7. Measurement & Monitoring',
  mdf8_installation: '8. Installation',
  mdf9_servicing: '9. Servicing',
  mdf10_risk_management_summary: '10. Risk Management Summary',
  mdf11_standards_applied: '11. Standards Applied',
  mdf12_index_and_revision_control: '12. Index & Revision Control',
};

/**
 * One grouped card: a plain title heading (not a numbered "Section N —"
 * heading — unlike the eSTAR/PMA renderers, the backend groups this package
 * as "Determinations" / "Medical Device File", not FDA Table-1 sections) +
 * every key in `keys`, unconditionally, each as a FieldProse row.
 */
function Group({
  title,
  keys,
  titles,
  data,
}: {
  title: string;
  keys: readonly string[];
  titles: Record<string, string>;
  data: Record<string, unknown>;
}) {
  return (
    <section className="bg-white border border-[#e0e0e0] rounded-xl p-[22px] text-[14px] text-ink leading-[1.55]">
      <h3 className="text-[17px] font-bold text-navy mt-0 mb-3 pb-1 border-b border-line">
        {title}
      </h3>
      <div>
        {keys.map((key) => {
          const fieldTitle = titles[key] ?? key;
          return (
            <FieldProse
              key={key}
              label={`${fieldTitle}:`}
              body={data[key]}
              fallbackHint={fieldTitle}
            />
          );
        })}
      </div>
    </section>
  );
}
